import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { INTENTS, loadCookiesData } from "~/utils";
import { withEntityCacheInvalidation } from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import {
  apiClient,
  AddUnitPlanPageLoaderResult,
  PropertyTableLoaderResult,
} from "~/services/apiClient";
import type { EntityListOptions } from "~/types";

export const AddUnitPlanPageLoader = async ({
  params,
}: LoaderFunctionArgs): Promise<AddUnitPlanPageLoaderType | Response> => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const propertyID = params.propertyID || "";

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  if (propertyID) {
    const result = await apiClient.loader<AddUnitPlanPageLoaderResult>(
      "add-unit-plan-page",
      {
        propertyID,
        ownerID,
        ownerType,
        userID: user.uid,
      },
    );

    if (!result.success || !result.data) {
      throw new Error(result.error ?? "Failed to load add unit plan page");
    }

    return {
      property: result.data.property,
      propertyOptions: [],
      canCreateUnitPlan:
        isAdminOrSuperAdmin || result.data.canCreateUnitPlan,
    };
  }

  const propertiesResult = await apiClient.loader<PropertyTableLoaderResult>(
    "property-table",
    { ownerID, ownerType },
  );

  const propertyOptions: EntityListOptions[] = (
    propertiesResult.data?.properties ?? []
  ).map((p) => ({
    value: p.id,
    text: p.name,
  }));

  return {
    property: null,
    propertyOptions,
    canCreateUnitPlan: isAdminOrSuperAdmin,
  };
};

export type AddUnitPlanPageLoaderType = {
  property: APITypes.Property | null;
  propertyOptions: EntityListOptions[];
  canCreateUnitPlan: boolean;
};

export const AddUnitPlanPageAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success || !authAccessInfo.data) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;
  const routePropertyID = params.propertyID || "";

  if (intent !== INTENTS._add_unit_plan) {
    return {};
  }

  const rawBody = formData.get("body") as string | null;
  if (!rawBody) return {};

  let formValues: Record<string, unknown> = {};
  try {
    formValues = JSON.parse(rawBody) as Record<string, unknown>;
  } catch {
    return {};
  }

  const propertyID =
    routePropertyID || (formValues.propertyID as string | undefined) || "";
  if (!propertyID) {
    return { success: false, error: "propertyID is required" };
  }

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const result = await withEntityCacheInvalidation(
    [{ entity: "unit-plan" }],
    async () =>
      apiClient.sendIntent<APITypes.UnitPlan>(INTENTS._add_unit_plan, {
        ...formValues,
        propertyID,
        ownerID,
        ownerType,
        userID: user.uid,
        isAdminOrSuperAdmin,
      }),
  );

  if (!result.success || !result.data?.id) {
    return {
      success: false,
      error: result.error ?? "Failed to create unit plan",
    };
  }

  return redirect(`/property/${propertyID}/unit-plan/${result.data.id}`);
};
